import { useCallback } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';
import ListWithTooltip from '../listWithTooltip';
import Tooltip from '../tooltip';
import Questionnaire from '../questionnaire';
import { formatDate } from '../../../utility/dateUtils';

const useStyles = makeStyles(
  ({ palette: { primary }, spacing, breakpoints }) => ({
    root: {
      borderBottom: `1px solid ${primary.bluish8}`,
      display: 'grid',
      gridColumnGap: 12,
      gridAutoFlow: 'column',
      gridAutoColumns: 'minmax(0,1fr)',
      paddingTop: spacing(8),
      paddingBottom: spacing(8),
      [breakpoints.up('xLg')]: {
        gridColumnGap: 24,
      },
    },
    labelWrapper: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: spacing(2),
    },
    label: {
      color: primary.bluish3,
      lineHeight: '20px',
    },
    labelTooltipIcon: {
      marginLeft: spacing(1),
      width: 14,
      height: 14,
      fill: primary.bluish3,
    },
    userMoreItems: {
      backgroundColor: 'transparent',
      fontSize: 16,
      lineHeight: '20px',
      padding: spacing(0, 2),
      marginTop: spacing(0.5),
      maxWidth: 'fit-content',
    },
  })
);

const SurveyHistoryInfo = ({ translations, survey }) => {
  const classes = useStyles();
  const { subjects, attributes, sent_at, is_anonymous, questionnaire } = survey;

  const getVirtualAttributeItemSize = useCallback(
    (index, items) => (index === items.length - 1 ? 28 : 366),
    []
  );

  const renderLabel = (label, helpTooltipText) => (
    <div className={classes.labelWrapper}>
      <Typography className={classes.label} variant="body2">
        {label}
      </Typography>
      {helpTooltipText && (
        <Tooltip
          customIconClass={classes.labelTooltipIcon}
          text={helpTooltipText}
        />
      )}
    </div>
  );

  return (
    <div className={classes.root}>
      <div>
        {questionnaire ? (
          <Questionnaire
            label={translations.labels.questionnaire}
            name={questionnaire?.name}
          />
        ) : (
          <ListWithTooltip
            labelClass={classes.label}
            moreItemsClass={classes.userMoreItems}
            label={translations.labels.attributes}
            items={attributes}
            visibleItemsCount={3}
            asyncListProps={{
              position: 'top',
              isDisabled: true,
              shouldDisablePortal: false,
              getItemSize: getVirtualAttributeItemSize,
            }}
          />
        )}
      </div>
      <div>
        <ListWithTooltip
          labelClass={classes.label}
          moreItemsClass={classes.userMoreItems}
          label={translations.labels.subjects}
          items={subjects}
          visibleItemsCount={4}
          asyncListProps={{
            position: 'top',
            shouldDisablePortal: false,
            isDisabled: true,
          }}
          isUserList
          isReadOnly
        />
      </div>
      <div>
        {renderLabel(translations.labels.dateReceived)}
        <Typography variant="subtitle1">{formatDate(sent_at)}</Typography>
      </div>
      <div>
        {renderLabel(
          translations.labels.anonymous.label,
          is_anonymous ? translations.labels.anonymous.helpText : ''
        )}
        <Typography variant="subtitle1">
          {is_anonymous ? translations.labels.yes : translations.labels.no}
        </Typography>
      </div>
    </div>
  );
};

SurveyHistoryInfo.propTypes = {
  translations: PropTypes.object.isRequired,
  survey: PropTypes.shape({}).isRequired,
};

export default SurveyHistoryInfo;
